
import React, { useEffect, useState } from "react";
import { Shop } from "@/components/dashboard/shops/types";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useFormSettings } from "./hooks/form-settings";
import { FormHeader } from "./components/FormHeader";
import { DeleteFormDialog } from "./components/form-menu/DeleteFormDialog";
import { FormTabsContent } from "./components/form-menu/FormTabsContent";
import { useFormTabsNavigation } from "./hooks/form-settings/useFormTabsNavigation";
import { useFormValidation } from "./hooks/form-settings/useFormValidation";

interface FormMenuSectionProps {
  shop: Shop;
}

export const FormMenuSection: React.FC<FormMenuSectionProps> = ({ shop }) => {
  const {
    isEditing,
    setIsEditing,
    isSaving,
    formName,
    setFormName,
    ctaButtonText,
    setCtaButtonText,
    formFields,
    toggleFieldEnabled,
    toggleFieldRequired,
    deleteField,
    addNewField,
    updateFieldName,
    updateFieldType,
    handleSave,
    hasFormTemplate,
    forms,
    currentForm,
    setCurrentForm,
    formProductIds,
    setFormProductIds,
    isLoading,
    refreshForms,
    createNewFormTemplate
  } = useFormSettings(shop);
  
  // Dialog state for deletion
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  // Navigation hooks
  const {
    activeTab,
    setActiveTab,
    handleFormSelect,
    handleCancelEdit
  } = useFormTabsNavigation(
    setCurrentForm,
    setIsEditing,
    currentForm,
    setFormName,
    setCtaButtonText,
    setFormProductIds
  );
  
  // Validation hooks
  const {
    validateShopIdAndCreateForm,
    validateShopIdForSaving
  } = useFormValidation(
    createNewFormTemplate,
    setActiveTab,
    setIsEditing
  );
  
  // Check if shop.id is a valid UUID
  useEffect(() => {
    if (!shop.id || typeof shop.id !== 'string' || 
        !shop.id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      console.error(`ID de shop invalide détecté: ${shop.id}. Cela causera des erreurs lors de la création de formulaires.`);
    }
  }, [shop.id]);
  
  // Load forms on component mount
  useEffect(() => {
    refreshForms();
  }, [refreshForms]);
  
  // Function to create a new form
  const handleCreateNewForm = () => {
    return validateShopIdAndCreateForm(shop.id);
  };

  // Function to save the form
  const handleSaveForm = async () => {
    if (!validateShopIdForSaving(shop.id)) {
      return null;
    }
    
    // Ensure currentForm has the correct shop_id before saving
    if (currentForm) {
      const formWithShopId = { ...currentForm, shop_id: shop.id };
      setCurrentForm(formWithShopId);
    }
    
    const savedForm = await handleSave();
    if (savedForm) {
      refreshForms();
      setActiveTab("forms");
    }
    return savedForm;
  };
  
  return (
    <div className="md:col-span-2">
      <Card className="shadow-md border-0 dark:border dark:border-gray-800">
        <CardHeader>
          <FormHeader 
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            isSaving={isSaving}
          />
        </CardHeader>
        <CardContent>
          <FormTabsContent 
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isEditing={isEditing}
            isSaving={isSaving}
            ctaButtonText={ctaButtonText}
            setCtaButtonText={setCtaButtonText}
            formName={formName}
            setFormName={setFormName}
            formFields={formFields}
            toggleFieldEnabled={toggleFieldEnabled}
            toggleFieldRequired={toggleFieldRequired}
            deleteField={deleteField}
            addNewField={addNewField}
            updateFieldName={updateFieldName}
            updateFieldType={updateFieldType}
            handleSaveForm={handleSaveForm}
            currentForm={currentForm}
            forms={forms}
            hasFormTemplate={hasFormTemplate}
            onSelectForm={handleFormSelect}
            handleCreateNewForm={handleCreateNewForm}
            isLoading={isLoading}
            formProductIds={formProductIds}
            setFormProductIds={setFormProductIds}
            handleCancelEdit={handleCancelEdit}
            shopId={shop.id}
            setIsDeleteDialogOpen={setIsDeleteDialogOpen}
          />
          
          <DeleteFormDialog
            isOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            form={currentForm}
            onSuccess={() => {
              setCurrentForm(null);
              refreshForms();
              setActiveTab("forms");
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};
